import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { supabase } from './supabase'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const UserPage = lazy(() => import('./pages/UserPage'))

const S = {
  center: { display:'flex', justifyContent:'center', alignItems:'center', height:'100vh', flexDirection:'column', gap:14, fontFamily:'inherit', background:'var(--bg)' },
  spinner: { width:36, height:36, border:'3px solid var(--border)', borderTop:'3px solid #7a2e3b', borderRadius:'50%', animation:'spin 0.8s linear infinite' },
  logo: { width:120, height:120, objectFit:'contain', marginBottom:8 }
}

// 다크모드에 따라 아이콘 선택
function Logo({ size = 120 }) {
  const [dark, setDark] = useState(window.matchMedia('(prefers-color-scheme: dark)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e) => setDark(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return <img src={dark ? '/icon-dark.png' : '/icon-light.png'} alt="하임청년부" style={{ width:size, height:size, objectFit:'contain' }} />
}

// ─── 홈 화면 바로가기 배너 ──────────────────────────────
function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [showBanner, setShowBanner] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    // 이미 설치된 상태면 안 보여줌
    if (window.matchMedia('(display-mode: standalone)').matches) return
    if (window.navigator.standalone) return

    // 닫은 적 있으면 7일간 안 보여줌
    const dismissed = localStorage.getItem('install_dismissed')
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return

    // iOS 체크
    const ua = navigator.userAgent
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    if (ios) {
      setIsIOS(true)
      setShowBanner(true)
      return
    }

    // Android / Chrome - beforeinstallprompt 이벤트
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setShowBanner(true) }
    window.addEventListener('beforeinstallprompt', handler)

    // 이벤트가 안 오더라도 모바일이면 배너 표시 (수동 안내)
    const isMobile = /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
    if (isMobile) {
      setTimeout(() => setShowBanner(true), 2000)
    }

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') setShowBanner(false)
    setDeferredPrompt(null)
  }

  const dismiss = () => {
    setShowBanner(false)
    localStorage.setItem('install_dismissed', String(Date.now()))
  }

  if (!showBanner) return null

  return (
    <div style={{
      position:'fixed', bottom:0, left:0, right:0, zIndex:200,
      background:'var(--card)', borderTop:'1px solid var(--border)', padding:'14px 16px',
      paddingBottom:'max(14px, env(safe-area-inset-bottom))',
      boxShadow:'var(--shadow-lg)',
      display:'flex', alignItems:'center', gap:12
    }}>
      <div style={{ width:44, height:44, borderRadius:12, background:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
        ⛪
      </div>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, fontSize:14, color:'var(--text)' }}>홈 화면에 바로가기 추가</div>
        {isIOS ? (
          <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.5 }}>
            하단 <strong>공유 버튼</strong>(□↑) → <strong>"홈 화면에 추가"</strong>
          </div>
        ) : deferredPrompt ? (
          <div style={{ fontSize:12, color:'var(--muted)' }}>앱처럼 바로 실행할 수 있어요</div>
        ) : (
          <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.5 }}>
            우측 상단 <strong>메뉴(⋮)</strong> → <strong>"홈 화면에 추가"</strong>
          </div>
        )}
      </div>
      {!isIOS && deferredPrompt && (
        <button onClick={handleInstall} style={{
          background:'var(--primary)', color:'#fff8ee', border:'none', borderRadius:10,
          padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', flexShrink:0
        }}>바로가기 추가</button>
      )}
      <button onClick={dismiss} style={{
        background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:18, padding:4, lineHeight:1, flexShrink:0
      }}>×</button>
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('admin') // 관리자 계정이 사용자 화면도 볼 수 있게 하는 토글 (DB에는 반영 안 됨)
  const viewModeInitialized = useRef(false) // 로그인 시 1회만 기본값을 정하고, 이후 토큰 갱신 등으로는 건드리지 않음

  const fetchProfile = async (userId) => {
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).single()
      setProfile(data)
      // 로그인 시 1회: 마스터 계정은 관리자 화면, 서브 관리자는 사용자 화면으로 기본 진입
      if (data && !viewModeInitialized.current) {
        setViewMode(data.email === 'haim0191@haimyouth.com' ? 'admin' : 'user')
        viewModeInitialized.current = true
      }
    } catch {}
    setLoading(false)
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      // 로그인 유지가 꺼져 있으면 기존 세션 제거
      if (session && localStorage.getItem('keep_login') !== 'true') {
        const isNewLogin = sessionStorage.getItem('current_session')
        if (!isNewLogin) {
          supabase.auth.signOut()
          setLoading(false)
          return
        }
      }
      setSession(session)
      if (session) fetchProfile(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session)
      if (session) {
        sessionStorage.setItem('current_session', 'true')
        fetchProfile(session.user.id)
      } else { setProfile(null); setLoading(false); viewModeInitialized.current = false }
    })
    return () => subscription.unsubscribe()
  }, [])

  if (loading) return (
    <div style={S.center}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <Logo size={96} />
      <div style={{...S.spinner, width:28, height:28}} />
      <span style={{color:'var(--muted)', fontSize:12, fontWeight:500}}>로딩 중...</span>
    </div>
  )

  const PageFallback = <div style={S.center}><div style={{...S.spinner, width:28, height:28}} /></div>

  if (!session) return <Suspense fallback={PageFallback}><LoginPage /><InstallBanner /></Suspense>

  if (!profile) return (
    <div style={S.center}>
      <Logo size={96} />
      <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--primary-light,#f1e3cd)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, marginTop:4 }}>⏳</div>
      <p style={{color:'var(--text)', fontWeight:700, fontSize:15, marginTop:2}}>승인 대기 중입니다</p>
      <p style={{color:'var(--muted)', fontSize:13, textAlign:'center', lineHeight:1.7}}>관리자가 확인 후 승인해드릴게요!<br/>잠시만 기다려주세요</p>
      <button onClick={() => supabase.auth.signOut()}
        style={{marginTop:10, padding:'9px 22px', background:'var(--sub-bg)', color:'var(--muted)', border:'none', borderRadius:10, cursor:'pointer', fontSize:13, fontWeight:600}}>
        로그아웃
      </button>
    </div>
  )

  if (profile.role === 'admin') {
    return viewMode === 'admin'
      ? <Suspense fallback={PageFallback}><AdminPage profile={profile} onRefresh={() => fetchProfile(session.user.id)} viewMode={viewMode} setViewMode={setViewMode} /><InstallBanner /></Suspense>
      : <Suspense fallback={PageFallback}><UserPage profile={profile} viewMode={viewMode} setViewMode={setViewMode} /><InstallBanner /></Suspense>
  }
  return <Suspense fallback={PageFallback}><UserPage profile={profile} /><InstallBanner /></Suspense>
}
